import { useState, useMemo, useRef } from 'react';
import { useExpenses } from '../contexts/ExpenseContext';
import { useMode } from '../contexts/ModeContext';
import { CATEGORIES, MONTHS, TRAVEL_SUBCATEGORIES } from '../types';
import { 
  FileText, 
  Download,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  CalendarDays,
  Loader2,
  FileSpreadsheet,
  Trash2,
  Table2,
  FileImage
} from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Categories that count as INCOME
const INCOME_CATEGORIES = ['settlements'];

// Categories that count as EXPENSES
const EXPENSE_CATEGORIES = ['travel', 'fuel', 'meals', 'bol'];

export default function Reports() {
  const { expenses, isLoading, deleteExpense } = useExpenses();
  const { isFamily } = useMode();
  const [isDeleting, setIsDeleting] = useState(false);
  const netLabel = isFamily ? 'Monthly Surplus' : 'Net Profit';
  
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Get available years from data
  const availableYears = useMemo(() => {
    const years = new Set(expenses.map(e => e.year));
    years.add(currentDate.getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses]);

  // Filter expenses for selected month/year
  const monthExpenses = useMemo(() => {
    return expenses.filter(
      e => e.month === selectedMonth && e.year === selectedYear
    );
  }, [expenses, selectedMonth, selectedYear]);

  // Apply category filter
  const filteredExpenses = useMemo(() => {
    if (filterCategory === 'all') return monthExpenses;
    return monthExpenses.filter(e => e.category === filterCategory);
  }, [monthExpenses, filterCategory]);

  // Calculate totals by category for the month
  const categoryTotals = useMemo(() => {
    return CATEGORIES.reduce((acc, cat) => {
      const catExpenses = monthExpenses.filter(e => e.category === cat.value);
      acc[cat.value] = {
        count: catExpenses.length,
        amount: catExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
        expenses: catExpenses
      };
      return acc;
    }, {} as Record<string, { count: number; amount: number; expenses: typeof expenses }>);
  }, [monthExpenses]);

  // Calculate counts per month for the selected year (for overview)
  const monthlyOverview = useMemo(() => {
    const overview: { month: number; count: number; amount: number }[] = [];
    for (let m = 1; m <= 12; m++) {
      const monthData = expenses.filter(e => e.month === m && e.year === selectedYear);
      overview.push({
        month: m,
        count: monthData.length,
        amount: monthData.reduce((sum, e) => sum + (e.amount || 0), 0)
      });
    }
    return overview;
  }, [expenses, selectedYear]);

  // Calculate income and expenses
  const monthlyIncome = useMemo(() => {
    return monthExpenses
      .filter(e => INCOME_CATEGORIES.includes(e.category))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [monthExpenses]);

  const monthlyExpenseTotal = useMemo(() => {
    return monthExpenses
      .filter(e => EXPENSE_CATEGORIES.includes(e.category))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [monthExpenses]);

  const netProfit = monthlyIncome - monthlyExpenseTotal;

  // Navigate months
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Clear all expenses for selected month
  const clearMonth = async () => {
    if (monthExpenses.length === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ALL ${monthExpenses.length} entries from ${MONTHS[selectedMonth - 1]} ${selectedYear}?\n\nThis cannot be undone!`
    );
    
    if (!confirmed) return;
    
    setIsDeleting(true);
    let deleted = 0;
    
    for (const expense of monthExpenses) {
      try {
        await deleteExpense(expense.id);
        deleted++;
      } catch (e) {
        console.error('Failed to delete expense:', e);
      }
    }
    
    setIsDeleting(false);
    alert(`Deleted ${deleted} entries from ${MONTHS[selectedMonth - 1]} ${selectedYear}`);
  };

  // Export to CSV (mobile-friendly with shorter headers)
  const exportToCSV = () => {
    const dataToExport = filterCategory === 'all' ? monthExpenses : filteredExpenses;
    const filterLabel = filterCategory === 'all' ? '' : `_${filterCategory}`;
    const filterTotal = dataToExport.reduce((sum, e) => sum + (e.amount || 0), 0);
    
    const headers = ['Amt', 'Date', 'Type', 'Desc'];
    const rows = dataToExport
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map(e => [
        (e.amount || 0).toFixed(2),
        e.date?.slice(5) || '', // MM/DD only
        e.category?.slice(0, 4) || '', // Short category
        (e.description || '').slice(0, 20) // Truncated description
      ]);
    
    const csvContent = filterCategory === 'all' 
      ? [
          `${MONTHS[selectedMonth - 1]} ${selectedYear}`,
          `Income,$${monthlyIncome.toFixed(2)}`,
          `Expense,$${monthlyExpenseTotal.toFixed(2)}`,
          `${netLabel},$${netProfit.toFixed(2)}`,
          '',
          headers.join(','),
          ...rows.map(r => r.map(cell => `"${cell}"`).join(','))
        ].join('\n')
      : [
          `${MONTHS[selectedMonth - 1]} ${selectedYear} - ${CATEGORIES.find(c => c.value === filterCategory)?.label}`,
          `Total,$${filterTotal.toFixed(2)}`,
          `Entries,${dataToExport.length}`,
          '',
          headers.join(','),
          ...rows.map(r => r.map(cell => `"${cell}"`).join(','))
        ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${selectedYear}_${String(selectedMonth).padStart(2, '0')}${filterLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export as plain text (easiest to read on phone)
  const exportToText = () => {
    const dataToExport = filterCategory === 'all' ? monthExpenses : filteredExpenses;
    const filterLabel = filterCategory === 'all' ? '' : `_${filterCategory}`;
    const filterTotal = dataToExport.reduce((sum, e) => sum + (e.amount || 0), 0);
    const catInfo = CATEGORIES.find(c => c.value === filterCategory);

    const lines = filterCategory === 'all'
      ? [
          `📊 ${MONTHS[selectedMonth - 1]} ${selectedYear}`,
          `━━━━━━━━━━━━━━━━━━━━`,
          `✅ Income:   $${monthlyIncome.toFixed(2)}`,
          `❌ Expense:  $${monthlyExpenseTotal.toFixed(2)}`,
          `💰 ${netLabel}:      $${netProfit.toFixed(2)}`,
        ]
      : [
          `📊 ${MONTHS[selectedMonth - 1]} ${selectedYear}`,
          `🔍 Filter: ${catInfo?.icon} ${catInfo?.label}`,
          `━━━━━━━━━━━━━━━━━━━━`,
          `💵 Total:    $${filterTotal.toFixed(2)}`,
          `📝 Entries:  ${dataToExport.length}`,
        ];

    lines.push(``);
    lines.push(`📋 TRANSACTIONS`);
    lines.push(`━━━━━━━━━━━━━━━━━━━━`);

    const sortedExpenses = [...dataToExport].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    sortedExpenses.forEach(e => {
      const isIncome = INCOME_CATEGORIES.includes(e.category);
      const sign = isIncome ? '+' : '-';
      const shortDate = e.date?.slice(5) || '??/??';
      lines.push(`${shortDate} ${sign}$${(e.amount || 0).toFixed(2).padStart(7)} ${e.description?.slice(0, 25) || ''}`);
    });

    lines.push('');
    lines.push('━━━━━━━━━━━━━━━━━━━━');
    lines.push(`Total: ${dataToExport.length} entries`);

    const textContent = lines.join('\n');
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${selectedYear}_${String(selectedMonth).padStart(2, '0')}${filterLabel}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ============================================
  // EXCEL EXPORT - Full Report with Styling
  // ============================================
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Tax Expense Tracker';
    workbook.created = new Date();
    const monthName = MONTHS[selectedMonth - 1];
    
    // Color definitions
    const colors = {
      green: { argb: 'FF228B22' },      // Income header
      greenLight: { argb: 'FF90EE90' }, // Income subheader
      red: { argb: 'FFDC143C' },        // Expense header
      redLight: { argb: 'FFFFB6C1' },   // Expense subheader
      orange: { argb: 'FFFF8C00' },     // Fuel header
      orangeLight: { argb: 'FFFFD700' },// Fuel subheader
      purple: { argb: 'FF8B008B' },     // Travel header
      purpleLight: { argb: 'FFDDA0DD' },// Travel subheader
      yellow: { argb: 'FFFFD700' },     // Total rows
      blue: { argb: 'FF4169E1' },       // Meals header
      blueLight: { argb: 'FFB0C4DE' },  // Meals subheader
    };
    
    // Helper function to format currency
    const currencyFormat = '"$"#,##0.00';
    const percentFormat = '0.0%';
    
    // Calculate income by company
    const incomeByCompany: Record<string, number> = {};
    monthExpenses.filter(e => INCOME_CATEGORIES.includes(e.category)).forEach(e => {
      const company = e.company || e.description?.split(' ')[0] || 'Other';
      incomeByCompany[company] = (incomeByCompany[company] || 0) + (e.amount || 0);
    });
    
    // Calculate expenses by category
    const expensesByCategory: Record<string, number> = {};
    monthExpenses.filter(e => EXPENSE_CATEGORIES.includes(e.category)).forEach(e => {
      const cat = CATEGORIES.find(c => c.value === e.category);
      const label = cat?.label || e.category;
      expensesByCategory[label] = (expensesByCategory[label] || 0) + (e.amount || 0);
    });
    
    // Fuel breakdown by station
    const fuelByStation: Record<string, number> = {};
    monthExpenses.filter(e => e.category === 'fuel').forEach(e => {
      let station = 'Other';
      const desc = (e.description || '').toLowerCase();
      if (desc.includes('loves') || desc.includes("love's")) station = 'Loves';
      else if (desc.includes('pilot')) station = 'Pilot';
      else if (desc.includes('flying j')) station = 'Flying J';
      else if (desc.includes('cefco')) station = 'CEFCO';
      else if (desc.includes('bp')) station = 'BP';
      else if (desc.includes('kwik trip')) station = 'Kwik Trip';
      else if (desc.includes('murphy')) station = 'Murphy USA';
      else if (desc.includes('speedway')) station = 'Speedway';
      else if (desc.includes('sunoco')) station = 'Sunoco';
      else if (desc.includes('shell')) station = 'Shell';
      else if (desc.includes('exxon')) station = 'Exxon';
      else if (desc.includes('chevron')) station = 'Chevron';
      else if (desc.includes('ta ') || desc.includes('petro')) station = 'TA/Petro';
      else if (e.vendor) station = e.vendor;
      fuelByStation[station] = (fuelByStation[station] || 0) + (e.amount || 0);
    });
    
    // Travel breakdown by subcategory
    const travelByType: Record<string, number> = {};
    monthExpenses.filter(e => e.category === 'travel').forEach(e => {
      let type = 'Other';
      const desc = (e.description || '').toLowerCase();
      const subcat = e.subcategory;
      if (subcat === 'rental' || desc.includes('rental') || desc.includes('enterprise') || desc.includes('hertz') || desc.includes('budget')) type = 'Rental Car';
      else if (subcat === 'shuttle' || desc.includes('shuttle')) type = 'Shuttle';
      else if (subcat === 'hotel' || desc.includes('hotel') || desc.includes('inn') || desc.includes('suites') || desc.includes('motel')) type = 'Hotel';
      else if (subcat === 'uber' || desc.includes('uber')) type = 'Uber/Lyft';
      else if (subcat === 'lyft' || desc.includes('lyft')) type = 'Uber/Lyft';
      else if (subcat === 'greyhound' || desc.includes('greyhound') || desc.includes('bus')) type = 'Bus';
      else if (subcat === 'airfare' || desc.includes('airline') || desc.includes('flight')) type = 'Airfare';
      else if (subcat === 'parking' || desc.includes('parking')) type = 'Parking';
      else if (desc.includes('toll')) type = 'Tolls';
      else if (subcat) {
        const subcatInfo = TRAVEL_SUBCATEGORIES.find(s => s.value === subcat);
        type = subcatInfo?.label || subcat;
      }
      travelByType[type] = (travelByType[type] || 0) + (e.amount || 0);
    });
    
    // Meals breakdown by vendor
    const mealsByVendor: Record<string, number> = {};
    monthExpenses.filter(e => e.category === 'meals').forEach(e => {
      let vendor = 'Other';
      const desc = (e.description || '').toLowerCase();
      if (desc.includes('panera')) vendor = 'Panera';
      else if (desc.includes('mcdonald')) vendor = 'McDonalds';
      else if (desc.includes('denny')) vendor = "Denny's";
      else if (desc.includes('waffle')) vendor = 'Waffle House';
      else if (desc.includes('subway')) vendor = 'Subway';
      else if (desc.includes('chipotle')) vendor = 'Chipotle';
      else if (desc.includes('taco bell')) vendor = 'Taco Bell';
      else if (desc.includes('wendy')) vendor = "Wendy's";
      else if (desc.includes('burger king')) vendor = 'Burger King';
      else if (desc.includes('chick-fil-a') || desc.includes('chickfila')) vendor = 'Chick-fil-A';
      else if (desc.includes('starbucks')) vendor = 'Starbucks';
      else if (desc.includes('bucee') || desc.includes("buc-ee")) vendor = "Buc-ee's";
      else if (desc.includes('snack') || desc.includes('convenience')) vendor = 'Snacks';
      else if (desc.includes('deli')) vendor = 'Deli';
      else if (e.vendor) vendor = e.vendor;
      mealsByVendor[vendor] = (mealsByVendor[vendor] || 0) + (e.amount || 0);
    });
    
    const fuelTotal = Object.values(fuelByStation).reduce((a, b) => a + b, 0);
    const travelTotal = Object.values(travelByType).reduce((a, b) => a + b, 0);
    const mealsTotal = Object.values(mealsByVendor).reduce((a, b) => a + b, 0);
    
    // ============================================
    // SHEET 1: Daily Report
    // ============================================
    const ws1 = workbook.addWorksheet('Daily Report');
    ws1.columns = [
      { key: 'col1', width: 25 },
      { key: 'col2', width: 15 },
    ];
    
    let row = 1;
    
    // Title
    ws1.getCell(`A${row}`).value = 'Monthly Income & Expense Breakdown';
    ws1.getCell(`A${row}`).font = { bold: true, size: 14 };
    row += 2;
    
    // INCOME SOURCES - Green header
    ws1.getCell(`A${row}`).value = 'INCOME SOURCES';
    ws1.getCell(`B${row}`).value = '';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.green };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    // Income subheader
    ws1.getCell(`A${row}`).value = 'Source';
    ws1.getCell(`B${row}`).value = 'Total';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.greenLight };
      cell.font = { bold: true };
    });
    row++;
    
    // Income data
    Object.entries(incomeByCompany)
      .sort((a, b) => b[1] - a[1])
      .forEach(([source, amount]) => {
        ws1.getCell(`A${row}`).value = source;
        ws1.getCell(`B${row}`).value = amount;
        ws1.getCell(`B${row}`).numFmt = currencyFormat;
        row++;
      });
    
    // Total Income - highlighted
    ws1.getCell(`A${row}`).value = 'Total Income';
    ws1.getCell(`B${row}`).value = monthlyIncome;
    ws1.getCell(`B${row}`).numFmt = currencyFormat;
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    row += 2;
    
    // EXPENSE CATEGORIES - Red header
    ws1.getCell(`A${row}`).value = 'EXPENSE CATEGORIES';
    ws1.getCell(`B${row}`).value = '';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.red };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    ws1.getCell(`A${row}`).value = 'Category';
    ws1.getCell(`B${row}`).value = 'Total';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.redLight };
      cell.font = { bold: true };
    });
    row++;
    
    Object.entries(expensesByCategory)
      .sort((a, b) => b[1] - a[1])
      .forEach(([cat, amount]) => {
        ws1.getCell(`A${row}`).value = cat;
        ws1.getCell(`B${row}`).value = amount;
        ws1.getCell(`B${row}`).numFmt = currencyFormat;
        row++;
      });
    
    ws1.getCell(`A${row}`).value = 'Total Expenses';
    ws1.getCell(`B${row}`).value = monthlyExpenseTotal;
    ws1.getCell(`B${row}`).numFmt = currencyFormat;
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    row += 2;
    
    // Fuel Subcategories - Orange header
    ws1.getCell(`A${row}`).value = 'Fuel Subcategories';
    ws1.getRow(row).eachCell((cell) => {
      cell.font = { bold: true, size: 11 };
    });
    row++;
    
    ws1.getCell(`A${row}`).value = 'Fuel Station';
    ws1.getCell(`B${row}`).value = 'Total';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.orange };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(fuelByStation)
      .sort((a, b) => b[1] - a[1])
      .forEach(([station, amount]) => {
        ws1.getCell(`A${row}`).value = station;
        ws1.getCell(`B${row}`).value = amount;
        ws1.getCell(`B${row}`).numFmt = currencyFormat;
        row++;
      });
    
    ws1.getCell(`A${row}`).value = 'TOTAL';
    ws1.getCell(`B${row}`).value = fuelTotal;
    ws1.getCell(`B${row}`).numFmt = currencyFormat;
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    row += 2;
    
    // Travel Subcategories - Purple header
    ws1.getCell(`A${row}`).value = 'Travel Subcategories';
    ws1.getRow(row).eachCell((cell) => {
      cell.font = { bold: true, size: 11 };
    });
    row++;
    
    ws1.getCell(`A${row}`).value = 'Subcategory';
    ws1.getCell(`B${row}`).value = 'Total';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.purple };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(travelByType)
      .sort((a, b) => b[1] - a[1])
      .forEach(([type, amount]) => {
        ws1.getCell(`A${row}`).value = type;
        ws1.getCell(`B${row}`).value = amount;
        ws1.getCell(`B${row}`).numFmt = currencyFormat;
        row++;
      });
    
    ws1.getCell(`A${row}`).value = 'TOTAL';
    ws1.getCell(`B${row}`).value = travelTotal;
    ws1.getCell(`B${row}`).numFmt = currencyFormat;
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    row += 2;
    
    // Meals Subcategories - Blue header
    ws1.getCell(`A${row}`).value = 'Meals Subcategories';
    ws1.getRow(row).eachCell((cell) => {
      cell.font = { bold: true, size: 11 };
    });
    row++;
    
    ws1.getCell(`A${row}`).value = 'Restaurant';
    ws1.getCell(`B${row}`).value = 'Total';
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.blue };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(mealsByVendor)
      .sort((a, b) => b[1] - a[1])
      .forEach(([vendor, amount]) => {
        ws1.getCell(`A${row}`).value = vendor;
        ws1.getCell(`B${row}`).value = amount;
        ws1.getCell(`B${row}`).numFmt = currencyFormat;
        row++;
      });
    
    ws1.getCell(`A${row}`).value = 'TOTAL';
    ws1.getCell(`B${row}`).value = mealsTotal;
    ws1.getCell(`B${row}`).numFmt = currencyFormat;
    ws1.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    
    // ============================================
    // SHEET 2: Category Breakdown with Percentages
    // ============================================
    const ws2 = workbook.addWorksheet('Category Breakdown');
    ws2.columns = [
      { key: 'col1', width: 20 },
      { key: 'col2', width: 15 },
      { key: 'col3', width: 10 },
    ];
    
    row = 1;
    
    ws2.getCell(`A${row}`).value = 'Expense Breakdown by Category';
    ws2.getCell(`A${row}`).font = { bold: true, size: 14 };
    row += 2;
    
    ws2.getCell(`A${row}`).value = 'Category';
    ws2.getCell(`B${row}`).value = 'Amount';
    ws2.getCell(`C${row}`).value = '%';
    ws2.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.red };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(expensesByCategory)
      .sort((a, b) => b[1] - a[1])
      .forEach(([cat, amount]) => {
        const pct = monthlyExpenseTotal > 0 ? amount / monthlyExpenseTotal : 0;
        ws2.getCell(`A${row}`).value = cat;
        ws2.getCell(`B${row}`).value = amount;
        ws2.getCell(`B${row}`).numFmt = currencyFormat;
        ws2.getCell(`C${row}`).value = pct;
        ws2.getCell(`C${row}`).numFmt = percentFormat;
        row++;
      });
    row++;
    
    // Fuel by Station
    ws2.getCell(`A${row}`).value = 'Fuel Expenses by Station';
    ws2.getCell(`A${row}`).font = { bold: true, size: 12 };
    row++;
    
    ws2.getCell(`A${row}`).value = 'Station';
    ws2.getCell(`B${row}`).value = 'Amount';
    ws2.getCell(`C${row}`).value = '%';
    ws2.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.orange };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(fuelByStation)
      .sort((a, b) => b[1] - a[1])
      .forEach(([station, amount]) => {
        const pct = fuelTotal > 0 ? amount / fuelTotal : 0;
        ws2.getCell(`A${row}`).value = station;
        ws2.getCell(`B${row}`).value = amount;
        ws2.getCell(`B${row}`).numFmt = currencyFormat;
        ws2.getCell(`C${row}`).value = pct;
        ws2.getCell(`C${row}`).numFmt = percentFormat;
        row++;
      });
    row++;
    
    // Travel Breakdown
    ws2.getCell(`A${row}`).value = 'TRAVEL BREAKDOWN';
    ws2.getCell(`A${row}`).font = { bold: true, size: 12 };
    row++;
    
    ws2.getCell(`A${row}`).value = 'Type';
    ws2.getCell(`B${row}`).value = 'Amount';
    ws2.getCell(`C${row}`).value = '%';
    ws2.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.purple };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(travelByType)
      .sort((a, b) => b[1] - a[1])
      .forEach(([type, amount]) => {
        const pct = travelTotal > 0 ? amount / travelTotal : 0;
        ws2.getCell(`A${row}`).value = type;
        ws2.getCell(`B${row}`).value = amount;
        ws2.getCell(`B${row}`).numFmt = currencyFormat;
        ws2.getCell(`C${row}`).value = pct;
        ws2.getCell(`C${row}`).numFmt = percentFormat;
        row++;
      });
    row++;
    
    // Meals Breakdown
    ws2.getCell(`A${row}`).value = 'MEALS BREAKDOWN';
    ws2.getCell(`A${row}`).font = { bold: true, size: 12 };
    row++;
    
    ws2.getCell(`A${row}`).value = 'Restaurant';
    ws2.getCell(`B${row}`).value = 'Amount';
    ws2.getCell(`C${row}`).value = '%';
    ws2.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.blue };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    Object.entries(mealsByVendor)
      .sort((a, b) => b[1] - a[1])
      .forEach(([vendor, amount]) => {
        const pct = mealsTotal > 0 ? amount / mealsTotal : 0;
        ws2.getCell(`A${row}`).value = vendor;
        ws2.getCell(`B${row}`).value = amount;
        ws2.getCell(`B${row}`).numFmt = currencyFormat;
        ws2.getCell(`C${row}`).value = pct;
        ws2.getCell(`C${row}`).numFmt = percentFormat;
        row++;
      });
    
    // ============================================
    // SHEET 3: All Transactions
    // ============================================
    const ws3 = workbook.addWorksheet('All Transactions');
    ws3.columns = [
      { key: 'date', width: 12 },
      { key: 'category', width: 15 },
      { key: 'subcategory', width: 12 },
      { key: 'description', width: 35 },
      { key: 'amount', width: 12 },
      { key: 'vendor', width: 20 },
    ];
    
    row = 1;
    ws3.getCell(`A${row}`).value = `All Transactions - ${monthName} ${selectedYear}`;
    ws3.getCell(`A${row}`).font = { bold: true, size: 14 };
    row += 2;
    
    // Header row
    ws3.getCell(`A${row}`).value = 'Date';
    ws3.getCell(`B${row}`).value = 'Category';
    ws3.getCell(`C${row}`).value = 'Subcategory';
    ws3.getCell(`D${row}`).value = 'Description';
    ws3.getCell(`E${row}`).value = 'Amount';
    ws3.getCell(`F${row}`).value = 'Company/Vendor';
    ws3.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });
    row++;
    
    [...monthExpenses]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .forEach(e => {
        const cat = CATEGORIES.find(c => c.value === e.category);
        const isIncome = INCOME_CATEGORIES.includes(e.category);
        ws3.getCell(`A${row}`).value = e.date;
        ws3.getCell(`B${row}`).value = cat?.label || e.category;
        ws3.getCell(`C${row}`).value = e.subcategory || '';
        ws3.getCell(`D${row}`).value = e.description || '';
        ws3.getCell(`E${row}`).value = e.amount || 0;
        ws3.getCell(`E${row}`).numFmt = currencyFormat;
        ws3.getCell(`E${row}`).font = { color: { argb: isIncome ? 'FF228B22' : 'FFDC143C' } };
        ws3.getCell(`F${row}`).value = e.company || e.vendor || '';
        row++;
      });
    
    row++;
    ws3.getCell(`D${row}`).value = 'TOTAL INCOME:';
    ws3.getCell(`E${row}`).value = monthlyIncome;
    ws3.getCell(`E${row}`).numFmt = currencyFormat;
    ws3.getRow(row).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FF228B22' } };
    });
    row++;
    
    ws3.getCell(`D${row}`).value = 'TOTAL EXPENSES:';
    ws3.getCell(`E${row}`).value = monthlyExpenseTotal;
    ws3.getCell(`E${row}`).numFmt = currencyFormat;
    ws3.getRow(row).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFDC143C' } };
    });
    row++;
    
    ws3.getCell(`D${row}`).value = 'NET PROFIT:';
    ws3.getCell(`E${row}`).value = netProfit;
    ws3.getCell(`E${row}`).numFmt = currencyFormat;
    ws3.getRow(row).eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: colors.yellow };
      cell.font = { bold: true };
    });
    
    // ============================================
    // Generate and download the file
    // ============================================
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filename = `${monthName}_${selectedYear}_Expenses.xlsx`;
    saveAs(blob, filename);
  };

  // ============================================
  // PDF EXPORT WITH CHARTS
  // ============================================
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showChartPreview, setShowChartPreview] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Family-mode categories for "Provisions by Store"
  const PROVISIONS_STORE_CATEGORIES = ['provisions', 'dining_family'];

  // Calculate chart data
  const chartData = useMemo(() => {
    // Expenses by category
    const expensesByCategory: Record<string, number> = {};
    const categoriesToUse = isFamily
      ? [...EXPENSE_CATEGORIES, 'provisions', 'utilities', 'connectivity', 'dining_family', 'auto_personal']
      : EXPENSE_CATEGORIES;
    monthExpenses.filter(e => categoriesToUse.includes(e.category)).forEach(e => {
      const cat = CATEGORIES.find(c => c.value === e.category);
      const label = cat?.label || e.category.replace(/_/g, ' ');
      expensesByCategory[label] = (expensesByCategory[label] || 0) + (e.amount || 0);
    });

    // Fuel by station (business) / Provisions by store (family)
    const fuelByStation: Record<string, number> = {};
    const provisionsByStore: Record<string, number> = {};
    if (isFamily) {
      monthExpenses.filter(e => PROVISIONS_STORE_CATEGORIES.includes(e.category as string)).forEach(e => {
        let store = 'Other';
        const desc = (e.description || '').toLowerCase();
        if (desc.includes('h-e-b') || desc.includes('heb')) store = 'H-E-B';
        else if (desc.includes('walmart')) store = 'Walmart';
        else if (desc.includes('kroger')) store = 'Kroger';
        else if (desc.includes('target')) store = 'Target';
        else if (desc.includes('costco')) store = 'Costco';
        else if (desc.includes('safeway')) store = 'Safeway';
        else if (desc.includes('grocery')) store = 'Grocery';
        else if ((e as { vendor?: string }).vendor) store = (e as { vendor?: string }).vendor;
        else if (e.description) store = e.description.slice(0, 30);
        provisionsByStore[store] = (provisionsByStore[store] || 0) + (e.amount || 0);
      });
    } else {
      monthExpenses.filter(e => e.category === 'fuel').forEach(e => {
        let station = 'Other';
        const desc = (e.description || '').toLowerCase();
        if (desc.includes('loves') || desc.includes("love's")) station = 'Loves';
        else if (desc.includes('pilot')) station = 'Pilot';
        else if (desc.includes('flying j')) station = 'Flying J';
        else if (desc.includes('cefco')) station = 'CEFCO';
        else if (desc.includes('bp')) station = 'BP';
        else if (desc.includes('kwik trip')) station = 'Kwik Trip';
        else if (desc.includes('murphy')) station = 'Murphy USA';
        else if (desc.includes('speedway')) station = 'Speedway';
        else if (desc.includes('sunoco')) station = 'Sunoco';
        else if (e.vendor) station = e.vendor;
        fuelByStation[station] = (fuelByStation[station] || 0) + (e.amount || 0);
      });
    }

    // Travel by type (business OTR / family personal trips)
    const travelByType: Record<string, number> = {};
    monthExpenses.filter(e => e.category === 'travel').forEach(e => {
      let type = 'Other';
      const desc = (e.description || '').toLowerCase();
      const subcat = e.subcategory;
      if (subcat === 'rental' || desc.includes('rental')) type = 'Rental Car';
      else if (subcat === 'shuttle' || desc.includes('shuttle')) type = 'Shuttle';
      else if (subcat === 'hotel' || desc.includes('hotel')) type = 'Hotel';
      else if (subcat === 'uber' || desc.includes('uber')) type = 'Uber/Lyft';
      else if (subcat === 'lyft' || desc.includes('lyft')) type = 'Uber/Lyft';
      else if (subcat === 'greyhound' || desc.includes('bus')) type = 'Bus';
      else if (desc.includes('toll')) type = 'Tolls';
      else if (subcat) {
        const subcatInfo = TRAVEL_SUBCATEGORIES.find(s => s.value === subcat);
        type = subcatInfo?.label || subcat;
      }
      travelByType[type] = (travelByType[type] || 0) + (e.amount || 0);
    });

    // Meals by vendor
    const mealsByVendor: Record<string, number> = {};
    monthExpenses.filter(e => e.category === 'meals').forEach(e => {
      let vendor = 'Other';
      const desc = (e.description || '').toLowerCase();
      if (desc.includes('panera')) vendor = 'Panera';
      else if (desc.includes('mcdonald')) vendor = 'McDonalds';
      else if (desc.includes('denny')) vendor = "Denny's";
      else if (desc.includes('chipotle')) vendor = 'Chipotle';
      else if (desc.includes('starbucks')) vendor = 'Starbucks';
      else if (desc.includes('bucee')) vendor = "Buc-ee's";
      else if (desc.includes('snack')) vendor = 'Snacks';
      else if (e.vendor) vendor = e.vendor;
      mealsByVendor[vendor] = (mealsByVendor[vendor] || 0) + (e.amount || 0);
    });

    return { expensesByCategory, fuelByStation, provisionsByStore, travelByType, mealsByVendor };
  }, [monthExpenses, isFamily]);

  // Chart colors
  const chartColors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
    '#FF9F40', '#FF6384', '#C9CBCF', '#7BC225', '#E91E63'
  ];

  // Pie chart data for expense categories
  const expensePieData = {
    labels: Object.keys(chartData.expensesByCategory),
    datasets: [{
      data: Object.values(chartData.expensesByCategory),
      backgroundColor: chartColors,
      borderWidth: 2,
      borderColor: '#1e293b',
    }]
  };

  // Bar chart: Fuel by Station (business) or Provisions by Store (family)
  const firstBarLabels = isFamily ? Object.keys(chartData.provisionsByStore) : Object.keys(chartData.fuelByStation);
  const firstBarValues = isFamily ? Object.values(chartData.provisionsByStore) : Object.values(chartData.fuelByStation);
  const fuelBarData = {
    labels: firstBarLabels,
    datasets: [{
      label: 'Amount ($)',
      data: firstBarValues,
      backgroundColor: isFamily ? '#2d5016' : '#FF8C00',
      borderColor: isFamily ? '#1e3d0f' : '#FF6600',
      borderWidth: 1,
    }]
  };

  // Bar chart data for travel
  const travelBarData = {
    labels: Object.keys(chartData.travelByType),
    datasets: [{
      label: 'Amount ($)',
      data: Object.values(chartData.travelByType),
      backgroundColor: '#8B008B',
      borderColor: '#6B006B',
      borderWidth: 1,
    }]
  };

  // Bar chart data for meals
  const mealsBarData = {
    labels: Object.keys(chartData.mealsByVendor),
    datasets: [{
      label: 'Amount ($)',
      data: Object.values(chartData.mealsByVendor),
      backgroundColor: '#4169E1',
      borderColor: '#2149C1',
      borderWidth: 1,
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { 
          callback: (value: number | string) => `$${value}`,
          color: '#333'
        },
        grid: { color: '#ddd' }
      },
      x: {
        ticks: { color: '#333' },
        grid: { display: false }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: { 
          color: '#333',
          font: { size: 11 }
        }
      },
    }
  };

  const exportToPDF = async () => {
    setShowChartPreview(true);
    setIsGeneratingPDF(true);
    
    // Wait for charts to render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const container = chartContainerRef.current;
      if (!container) {
        alert('Failed to generate PDF - chart container not found');
        return;
      }

      const canvas = await html2canvas(container, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      
      // Calculate dimensions to FIT ENTIRE content on ONE page
      const maxWidth = pageWidth - (margin * 2);
      const maxHeight = pageHeight - (margin * 2);
      
      // Calculate scale to fit both width and height
      const widthScale = maxWidth / (canvas.width / 2); // Divide by 2 because scale:2
      const heightScale = maxHeight / (canvas.height / 2);
      const scale = Math.min(widthScale, heightScale);
      
      const imgWidth = (canvas.width / 2) * scale;
      const imgHeight = (canvas.height / 2) * scale;
      
      // Center the image on the page
      const xOffset = (pageWidth - imgWidth) / 2;
      const yOffset = margin;
      
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      const monthName = MONTHS[selectedMonth - 1];
      pdf.save(`${monthName}_${selectedYear}_Report.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
      setShowChartPreview(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <FileSpreadsheet className="w-7 h-7 sm:w-8 sm:h-8 text-shuttle-sand" />
            Monthly Reports
          </h1>
          <p className="text-white/60 mt-1 text-sm sm:text-base">
            View detailed expense breakdowns by month
          </p>
        </div>
        {/* Export Buttons - Scrollable on mobile */}
        <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-500/20 text-green-400 border border-green-500/50 rounded-xl hover:bg-green-500/30 transition-colors text-sm whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 rounded-xl hover:bg-emerald-500/30 transition-colors text-sm whitespace-nowrap"
            >
              <Table2 className="w-4 h-4" />
              Excel
            </button>
            <button
              onClick={exportToPDF}
              disabled={isGeneratingPDF}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-xl hover:bg-purple-500/30 transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
            >
              {isGeneratingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileImage className="w-4 h-4" />}
              PDF
            </button>
            <button
              onClick={exportToText}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-xl hover:bg-blue-500/30 transition-colors text-sm whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              Text
            </button>
            {monthExpenses.length > 0 && (
              <button
                onClick={clearMonth}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
              >
                {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Year Overview - Quick view of which months have data */}
      <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white/80 text-sm font-medium">{selectedYear} Overview</h3>
          <span className="text-white/50 text-xs">
            {monthlyOverview.filter(m => m.count > 0).length}/12 months with data
          </span>
        </div>
        <div className="grid grid-cols-12 gap-1">
          {monthlyOverview.map((m) => (
            <button
              key={m.month}
              onClick={() => setSelectedMonth(m.month)}
              className={`p-2 rounded-lg text-center transition-all ${
                m.month === selectedMonth 
                  ? 'bg-shuttle-sand text-shuttle-dark ring-2 ring-shuttle-sand' 
                  : m.count > 0 
                    ? 'bg-emerald-500/30 text-emerald-300 hover:bg-emerald-500/50' 
                    : 'bg-white/5 text-white/30 hover:bg-white/10'
              }`}
              title={`${MONTHS[m.month - 1]}: ${m.count} entries, $${m.amount.toFixed(0)}`}
            >
              <div className="text-xs font-medium">{MONTHS[m.month - 1].slice(0, 3)}</div>
              {m.count > 0 && (
                <div className={`text-[10px] mt-0.5 ${m.month === selectedMonth ? 'text-shuttle-dark/80' : 'text-inherit opacity-80'}`}>
                  {m.count}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Month Selector */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousMonth}
            className="p-3 hover:bg-white/10 rounded-xl transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-lg font-medium focus:outline-none focus:ring-2 focus:ring-shuttle-sand"
            >
              {MONTHS.map((month, idx) => {
                const monthData = monthlyOverview[idx];
                return (
                  <option key={month} value={idx + 1} className="bg-slate-800">
                    {month} {monthData?.count > 0 ? `(${monthData.count})` : ''}
                  </option>
                );
              })}
            </select>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-lg font-medium focus:outline-none focus:ring-2 focus:ring-shuttle-sand"
            >
              {availableYears.map(year => (
                <option key={year} value={year} className="bg-slate-800">
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          <button
            onClick={goToNextMonth}
            className="p-3 hover:bg-white/10 rounded-xl transition-colors"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Category Filter */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-white/60 text-sm mb-3">Filter by Category</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'all'
                  ? 'bg-shuttle-sand text-slate-900'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(cat => {
              const hasData = monthExpenses.some(e => e.category === cat.value);
              return (
                <button
                  key={cat.value}
                  onClick={() => setFilterCategory(cat.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    filterCategory === cat.value
                      ? 'bg-shuttle-sand text-slate-900'
                      : hasData
                        ? 'bg-white/10 text-white/70 hover:bg-white/20'
                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                  }`}
                  disabled={!hasData}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Income */}
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl p-5 border border-green-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-white/70 text-sm">Income (Settlements)</span>
          </div>
          <p className="text-3xl font-bold text-green-400">
            ${monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-white/50 text-sm mt-1">
            {categoryTotals['settlements']?.count || 0} settlements
          </p>
        </div>

        {/* Expenses */}
        <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-2xl p-5 border border-red-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-white/70 text-sm">Total Expenses</span>
          </div>
          <p className="text-3xl font-bold text-red-400">
            ${monthlyExpenseTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-white/50 text-sm mt-1">
            {monthExpenses.filter(e => EXPENSE_CATEGORIES.includes(e.category)).length} transactions
          </p>
        </div>

        {/* Net Profit / Monthly Surplus */}
        <div className={`bg-gradient-to-br ${netProfit >= 0 ? 'from-blue-500/20 to-blue-600/10 border-blue-500/30' : 'from-orange-500/20 to-orange-600/10 border-orange-500/30'} rounded-2xl p-5 border`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 ${netProfit >= 0 ? 'bg-blue-500/20' : 'bg-orange-500/20'} rounded-lg`}>
              <Wallet className={`w-5 h-5 ${netProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`} />
            </div>
            <span className="text-white/70 text-sm">{netLabel}</span>
          </div>
          <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            {netProfit >= 0 ? '+' : '-'}${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-white/50 text-sm mt-1">
            After expenses
          </p>
        </div>

        {/* Total Entries */}
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl p-5 border border-purple-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <CalendarDays className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-white/70 text-sm">Total Entries</span>
          </div>
          <p className="text-3xl font-bold text-purple-400">
            {monthExpenses.length}
          </p>
          <p className="text-white/50 text-sm mt-1">
            This month
          </p>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-shuttle-sand" />
          Category Breakdown
        </h2>
        
        <div className="space-y-3">
          {CATEGORIES.map(cat => {
            const data = categoryTotals[cat.value];
            if (!data || data.count === 0) return null;
            
            const isIncome = INCOME_CATEGORIES.includes(cat.value);
            const percentage = isIncome 
              ? (data.amount / (monthlyIncome || 1)) * 100
              : (data.amount / (monthlyExpenseTotal || 1)) * 100;
            
            return (
              <div key={cat.value} className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <div>
                      <p className="text-white font-medium">{cat.label}</p>
                      <p className="text-white/50 text-sm">{data.count} entries</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xl font-bold ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                      {isIncome ? '+' : '-'}${data.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-white/50 text-sm">{percentage.toFixed(1)}%</p>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${isIncome ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
          
          {monthExpenses.length === 0 && (
            <div className="text-center py-8 text-white/50">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No entries for {MONTHS[selectedMonth - 1]} {selectedYear}</p>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Transactions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-shuttle-sand" />
          Transactions ({filteredExpenses.length})
          {filterCategory !== 'all' && (
            <span className="text-sm font-normal text-white/50">
              — {CATEGORIES.find(c => c.value === filterCategory)?.label}
            </span>
          )}
        </h2>

        {/* Filter Summary Card */}
        {filterCategory !== 'all' && (
          <div className="mb-4 p-4 bg-shuttle-sand/10 border border-shuttle-sand/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">
                  {CATEGORIES.find(c => c.value === filterCategory)?.icon} {CATEGORIES.find(c => c.value === filterCategory)?.label} Total
                </p>
                <p className="text-2xl font-bold text-shuttle-sand">
                  ${filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-white/50 text-sm">{filteredExpenses.length} entries</p>
                <button
                  onClick={() => setFilterCategory('all')}
                  className="text-shuttle-sand text-sm hover:underline"
                >
                  Clear filter
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/70 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">Category</th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">Description</th>
                <th className="text-right py-3 px-4 text-white/70 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .slice(0, 50)
                .map(expense => {
                  const isIncome = INCOME_CATEGORIES.includes(expense.category);
                  const cat = CATEGORIES.find(c => c.value === expense.category);
                  return (
                    <tr key={expense.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white/80">{expense.date}</td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2">
                          <span>{cat?.icon}</span>
                          <span className="text-white/80">{cat?.label}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-white/70 max-w-xs truncate">
                        {expense.description}
                      </td>
                      <td className={`py-3 px-4 text-right font-medium ${isIncome ? 'text-green-400' : 'text-red-400'}`}>
                        {isIncome ? '+' : '-'}${(expense.amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          
          {filteredExpenses.length > 50 && (
            <p className="text-white/50 text-sm text-center mt-4">
              Showing first 50 of {filteredExpenses.length} transactions
            </p>
          )}

          {filteredExpenses.length === 0 && filterCategory !== 'all' && (
            <div className="text-center py-8 text-white/50">
              <p>No {CATEGORIES.find(c => c.value === filterCategory)?.label} expenses this month</p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden Chart Container for PDF Generation */}
      {showChartPreview && (
        <div 
          ref={chartContainerRef}
          className="fixed left-[-9999px] top-0 bg-white p-4"
          style={{ width: '700px' }}
        >
          {/* Header */}
          <div className="text-center mb-3">
            <h1 className="text-lg font-bold text-gray-800">
              {MONTHS[selectedMonth - 1]} {selectedYear} Expense Report
            </h1>
            <p className="text-gray-500 text-xs">Generated by Tax Expense Tracker</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-green-100 p-2 rounded border border-green-300">
              <p className="text-green-800 font-medium text-[10px]">Total Income</p>
              <p className="text-base font-bold text-green-600">${monthlyIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="bg-red-100 p-2 rounded border border-red-300">
              <p className="text-red-800 font-medium text-[10px]">Total Expenses</p>
              <p className="text-base font-bold text-red-600">${monthlyExpenseTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
            <div className={`p-2 rounded border ${netProfit >= 0 ? 'bg-blue-100 border-blue-300' : 'bg-orange-100 border-orange-300'}`}>
              <p className={`font-medium text-[10px] ${netProfit >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>{netLabel}</p>
              <p className={`text-base font-bold ${netProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                {netProfit >= 0 ? '+' : '-'}${Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {/* Expense Categories Pie Chart */}
          <div className="mb-4">
            <h2 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-red-500 pb-1">
              Expense Categories
            </h2>
            <div className="flex items-start">
              <div style={{ width: '220px', height: '150px' }}>
                <Pie data={expensePieData} options={pieOptions} />
              </div>
              <div className="ml-3 flex-1">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-red-500 text-white">
                      <th className="text-left p-1">Category</th>
                      <th className="text-right p-1">Amount</th>
                      <th className="text-right p-1">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(chartData.expensesByCategory)
                      .sort((a, b) => b[1] - a[1])
                      .map(([cat, amount], i) => (
                        <tr key={cat} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-1">{cat}</td>
                          <td className="text-right p-1">${amount.toFixed(2)}</td>
                          <td className="text-right p-1">{((amount / monthlyExpenseTotal) * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Charts in 2-column grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Fuel by Station (business) / Provisions by Store (family) Bar Chart */}
            {(isFamily ? Object.keys(chartData.provisionsByStore) : Object.keys(chartData.fuelByStation)).length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-orange-500 pb-1">
                  {isFamily ? 'Provisions by Store' : 'Fuel by Station'}
                </h2>
                <div style={{ height: '140px' }}>
                  <Bar data={fuelBarData} options={barOptions} />
                </div>
              </div>
            )}

            {/* Travel Breakdown Bar Chart */}
            {Object.keys(chartData.travelByType).length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-purple-500 pb-1">
                  Travel Breakdown
                </h2>
                <div style={{ height: '140px' }}>
                  <Bar data={travelBarData} options={barOptions} />
                </div>
              </div>
            )}
          </div>

          {/* Meals Breakdown - Full width */}
          {Object.keys(chartData.mealsByVendor).length > 0 && (
            <div className="mt-3">
              <h2 className="text-sm font-bold text-gray-800 mb-2 border-b-2 border-blue-500 pb-1">
                Meals Breakdown
              </h2>
              <div style={{ height: '140px' }}>
                <Bar data={mealsBarData} options={barOptions} />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-gray-400 text-[10px] mt-3 pt-2 border-t">
            <p>Report generated on {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}

